import { useI18n } from "@/hooks/useI18n";
import { dictionary } from "@/lib/i18n";
import { MapPin, Phone as PhoneIcon, Mail, Clock } from "lucide-react";

// Yandex Maps embed centered on CADS office — ул. Байзакова 280, Алматы
const YANDEX_MAP_SRC =
  "https://yandex.com/map-widget/v1/?ll=76.91485431184155%2C43.2372472846149&z=18&pt=76.91485431184155%2C43.2372472846149%2Cpm2rdm&l=map";

export const Contacts = () => {
  const { lang } = useI18n();
  const data = dictionary.contacts;

  const addressValue =
    lang === "ru"
      ? data.address_value_ru
      : lang === "kz"
        ? data.address_value_kz
        : data.address_value_en;

  const blocks = [
    {
      icon: MapPin,
      label: data.address_label[lang],
      value: addressValue,
      testid: "contact-address",
    },
    {
      icon: PhoneIcon,
      label: data.phone_label[lang],
      value: data.phone_value,
      href: `tel:${data.phone_value.replace(/\s+/g, "")}`,
      testid: "contact-phone",
    },
    {
      icon: Mail,
      label: data.email_label[lang],
      value: data.email_value,
      href: `mailto:${data.email_value}`,
      testid: "contact-email",
    },
    {
      icon: Clock,
      label: data.hours_label[lang],
      value: data.hours_value[lang],
      testid: "contact-hours",
    },
  ];

  return (
    <section
      id="contacts"
      data-testid="contacts-section"
      className="relative py-24 md:py-28 bg-white border-b border-[#E2E8F0]"
    >
      <div className="container mx-auto px-6 md:px-10 max-w-7xl">
        <div className="mb-12">
          <span
            data-testid="contacts-label"
            className="text-[11px] font-semibold tracking-[0.3em] uppercase text-[#1857D6]"
          >
            ▍ {data.label[lang]}
          </span>
          <h2
            data-testid="contacts-title"
            className="mt-4 text-3xl sm:text-4xl lg:text-5xl font-black tracking-tight text-[#0A1933] leading-[1.1]"
            style={{ fontFamily: "Manrope, IBM Plex Sans, sans-serif" }}
          >
            {data.title[lang]}
          </h2>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 border border-[#E2E8F0]">
          <div className="lg:col-span-7 relative min-h-[420px] lg:min-h-[520px] bg-[#F5F8FC]">
            <iframe
              data-testid="contacts-map"
              title="CADS office on the map"
              src={YANDEX_MAP_SRC}
              loading="lazy"
              className="absolute inset-0 w-full h-full border-0"
              allow="fullscreen"
            />
            <div className="absolute bottom-4 left-4 right-4 sm:left-6 sm:right-auto sm:max-w-[300px] bg-white border border-[#E2E8F0] p-4 shadow-lg pointer-events-none">
              <div className="text-[10px] tracking-[0.25em] uppercase text-[#1857D6] font-bold">
                CADS · HQ
              </div>
              <div className="mt-1.5 text-sm font-semibold text-[#0A1933] leading-snug">
                {data.map_caption[lang]}
              </div>
            </div>
          </div>

          <div className="lg:col-span-5 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-1 bg-[#0F2756]">
            {blocks.map((block, idx) => {
              const Icon = block.icon;
              const Inner = (
                <>
                  <Icon className="h-5 w-5 text-[#4FC3F7]" />
                  <div className="mt-4">
                    <div className="text-[10px] tracking-[0.3em] text-white/55 font-semibold uppercase">
                      {block.label}
                    </div>
                    <div className="mt-1.5 text-base lg:text-[17px] text-white font-medium leading-snug">
                      {block.value}
                    </div>
                  </div>
                </>
              );
              return block.href ? (
                <a
                  key={idx}
                  href={block.href}
                  data-testid={block.testid}
                  className="border-b border-white/10 last:border-b-0 lg:border-b lg:border-r-0 p-6 lg:p-7 transition-colors hover:bg-[#13306B] flex flex-col"
                >
                  {Inner}
                </a>
              ) : (
                <div
                  key={idx}
                  data-testid={block.testid}
                  className="border-b border-white/10 last:border-b-0 p-6 lg:p-7 flex flex-col"
                >
                  {Inner}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contacts;
